/**
 * Utilities for generating calendar links and .ics file downloads.
 */

interface CalendarEventParams {
  title: string;
  startDate: string; // ISO string
  endDate?: string; // ISO string
  location?: string;
  description?: string;
  isAllDay?: boolean;
}

/**
 * Format a date for .ics files (UTC): YYYYMMDDTHHMMSSZ
 */
function formatIcsDate(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/**
 * Format a date for .ics all-day events: YYYYMMDD
 */
function formatIcsAllDay(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toISOString().slice(0, 10).replace(/-/g, "");
}

/**
 * Generate a Google Calendar URL for an event.
 */
export function generateGoogleCalendarUrl(params: CalendarEventParams): string {
  const { title, startDate, endDate, location, description, isAllDay } = params;

  const start = isAllDay ? formatIcsAllDay(startDate) : formatIcsDate(startDate);
  const end = endDate
    ? isAllDay ? formatIcsAllDay(endDate) : formatIcsDate(endDate)
    : start;

  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", title);
  url.searchParams.set("dates", `${start}/${end}`);
  if (location) url.searchParams.set("location", location);
  if (description) url.searchParams.set("details", description);

  return url.toString();
}

/**
 * Download an .ics file for an event.
 */
export function downloadIcsFile(params: CalendarEventParams): void {
  const { title, startDate, endDate, location, description, isAllDay } = params;

  const start = isAllDay
    ? `DTSTART;VALUE=DATE:${formatIcsAllDay(startDate)}`
    : `DTSTART:${formatIcsDate(startDate)}`;
  const end = endDate
    ? isAllDay
      ? `DTEND;VALUE=DATE:${formatIcsAllDay(endDate)}`
      : `DTEND:${formatIcsDate(endDate)}`
    : "";

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//TinyClub//Event//EN",
    "BEGIN:VEVENT",
    start,
    end,
    `SUMMARY:${escapeIcsText(title)}`,
    location ? `LOCATION:${escapeIcsText(location)}` : "",
    description ? `DESCRIPTION:${escapeIcsText(description)}` : "",
    `UID:${crypto.randomUUID()}@tinyclub.co`,
    `DTSTAMP:${formatIcsDate(new Date().toISOString())}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");

  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9]/g, "_")}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeIcsText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}
